import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { Groups as GroupsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../Utils/FormatNumber';
import { buildPartnersDistribution } from '../../Utils/partners/partnersEquityDistribution';
import modalStyles from './PartnersEquityModal.module.scss';

const AmountText = ({ value }) => (
    <span className={`${modalStyles.amount}${value < 0 ? ` ${modalStyles.negative}` : ''}`}>
        {beautifyNumber(value)}
    </span>
);

AmountText.propTypes = {
    value: PropTypes.number.isRequired,
};

const PartnersEquityModal = ({
    open,
    onClose,
    partners = [],
    total = 0,
    direction = 'rtl',
}) => {
    const { t } = useTranslation();
    const textAlign = direction === 'rtl' ? 'right' : 'left';

    const distribution = useMemo(
        () => buildPartnersDistribution(partners, total),
        [partners, total]
    );

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, onClose]);

    if (!open) return null;

    const equityIsComplete = Math.abs(distribution.totalEquity - 100) <= 0.01;

    return (
        <Portal>
            <div
                className={`${modalStyles.overlay} ${direction === 'rtl' ? modalStyles.rtl : modalStyles.ltr}`}
                onClick={onClose}
            >
                <div dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={modalStyles.modal}>
                        <div className={modalStyles.body}>
                            <div className={modalStyles.header}>
                                <Tooltip title={t('Close') || 'Close'}>
                                    <IconButton onClick={onClose} className={modalStyles.closeIcon}>
                                        <CancelIcon className={modalStyles.closeIcon} />
                                    </IconButton>
                                </Tooltip>
                                <div className={modalStyles.headerTitle}>
                                    <GroupsIcon />
                                    {t('PartnersDistribution')}
                                </div>
                                <div />
                            </div>

                            <div className={modalStyles.toolbar}>
                                <p className={modalStyles.muted}>{t('PartnersDistributionHelp')}</p>
                                <div className={modalStyles.totalChip}>
                                    {t('TotalToDistribute')}:
                                    <span className={modalStyles.totalChipValue}>
                                        <AmountText value={distribution.total} />
                                    </span>
                                </div>
                            </div>

                            <div className={modalStyles.scroll}>
                                {distribution.rows.length === 0 ? (
                                    <div className={modalStyles.emptyState}>{t('NoPartnersFound')}</div>
                                ) : (
                                    <div className={modalStyles.tableWrap}>
                                        <TableContainer component={Paper} variant="outlined">
                                            <Table size="small" dir={direction}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell align={textAlign}>{t('PartnerName')}</TableCell>
                                                        <TableCell align={textAlign}>{t('PartnerId')}</TableCell>
                                                        <TableCell align={textAlign}>{t('Equity')} (%)</TableCell>
                                                        <TableCell align={textAlign}>{t('Total')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {distribution.rows.map((row) => (
                                                        <TableRow key={row.id} hover>
                                                            <TableCell align={textAlign}>{row.name}</TableCell>
                                                            <TableCell align={textAlign}>{row.partnerId}</TableCell>
                                                            <TableCell align={textAlign}>{row.equity.toFixed(2)}%</TableCell>
                                                            <TableCell align={textAlign}>
                                                                <AmountText value={row.amount} />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                    <TableRow className={modalStyles.totalRow}>
                                                        <TableCell align={textAlign}>{t('Total')}</TableCell>
                                                        <TableCell align={textAlign} />
                                                        <TableCell
                                                            align={textAlign}
                                                            className={equityIsComplete ? modalStyles.equityOk : modalStyles.equityWarn}
                                                        >
                                                            {distribution.totalEquity.toFixed(2)}%
                                                        </TableCell>
                                                        <TableCell align={textAlign}>
                                                            <AmountText value={distribution.totalAmount} />
                                                        </TableCell>
                                                    </TableRow>
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                )}
                            </div>

                            <div className={modalStyles.footer}>
                                <button type="button" className={modalStyles.secondaryBtn} onClick={onClose}>
                                    {t('Close') || 'Close'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

PartnersEquityModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    partners: PropTypes.array,
    total: PropTypes.number,
    direction: PropTypes.string,
};

export default PartnersEquityModal;
